import { FunctionComponent, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { ArrowRightIcon } from '@heroicons/react/solid';
import { useAuth0 } from '@auth0/auth0-react';

import { useUserInfo } from '@/features/userInfo/hooks';
import { WorkspaceInviteRow } from './WorkspaceInviteRow';
import { useAcceptWorkspaceInviteMutation } from '@/services/endpoints/workspaces';
import { useAppDispatch } from '@/reducers';
import { storeToken } from '@/features/auth/authSlice';
import { showToast } from '@/features/toasts/toastsSlice';
import { ToastType } from '@/models/toast';
import { PostelLogo } from '@/features/layout/PostelLogo';
import { AudiencerateLogo } from '@/features/layout/AudienceRateLogo';
import { ReturnToLoginButton } from '@/features/resetPassword/components/ReturnToLoginButton/ReturnToLoginButton';

export const WorkspaceInvites: FunctionComponent = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const userInfo = useUserInfo();
  const { logout, getAccessTokenSilently } = useAuth0();

  const [selectedWorkspace, setSelectedWorkspace] = useState<string | null>(null);

  const [acceptInvitation, { isLoading }] = useAcceptWorkspaceInviteMutation();

  const API_COMPANY = import.meta.env.VITE_COMPANY;

  const isPostel = API_COMPANY === 'postel';
  const isAudienceRate = API_COMPANY === 'audiencerate';

  const pendingInvites = useMemo(() => userInfo.workspaceInvites?.filter((invite) => invite.status === 'PENDING') ?? [], [userInfo.workspaceInvites]);

  const hasPendingInvites = pendingInvites.length > 0;

  const handleAccept = async () => {
    if (!selectedWorkspace) return;

    const invite = pendingInvites.find((pendingInvite) => pendingInvite.workspaceId === selectedWorkspace);

    if (!invite) return;

    try {
      await acceptInvitation({ inviteId: invite.id }).unwrap();

      const token = await getAccessTokenSilently({ cacheMode: 'off' });
      dispatch(storeToken(token));

      dispatch(
        showToast({
          type: ToastType.SUCCESS,
          title: t('home:enterWorkspaceLabel'),
        })
      );

      window.location.reload();
    } catch (error) {
      logout({
        logoutParams: { returnTo: window.location.origin },
      });
    }
  };

  /*
  const year = new Date().getFullYear();

  const termsUrl = termsData?.documents?.terms_and_conditions?.url ?? '#';
  const privacyUrl = termsData?.documents?.privacy_policy?.url ?? '#';
*/
  return (
    <div className='postel-bg-color flex min-h-screen flex-col'>
      <div className='flex flex-1 flex-col p-8'>
        <header className='flex items-start justify-between gap-4'>
          {isPostel && (
            <>
              <div>
                <PostelLogo width={100} />
                <p className='text-xs'>
                  powered by <b>Audiencerate</b>
                </p>
              </div>

              <div className='ml-auto'>
                <ReturnToLoginButton />
              </div>
            </>
          )}

          {isAudienceRate && <AudiencerateLogo />}
        </header>

        <main className='flex flex-1 items-center justify-center px-12 py-14'>
          {hasPendingInvites ? (
            <div className='w-full max-w-2xl'>
              <h1 className='text-center text-xl font-bold text-blue-900'>
                <Trans i18nKey='home:workspacePageWelcomeMessage' />
              </h1>

              <p className='text-center text-base text-gray-600'>
                <Trans i18nKey='ALL:welcomeWorkspaceParagraph' />
              </p>

              <div className='mt-6 space-y-2'>
                {pendingInvites.map((invite) => (
                  <WorkspaceInviteRow key={invite.workspaceId} invite={invite} selectedWorkspace={selectedWorkspace} onSelect={setSelectedWorkspace} />
                ))}
              </div>

              <div className='mt-6 flex justify-center'>
                <button
                  onClick={handleAccept}
                  disabled={!selectedWorkspace || isLoading}
                  className={`flex w-full items-center justify-center rounded-md p-3 text-white-100 transition ${
                    selectedWorkspace ? 'bg-blue-900 hover:bg-blue-800' : 'cursor-not-allowed bg-gray-400'
                  }`}
                >
                  {isLoading ? (
                    'Loading...'
                  ) : (
                    <>
                      <Trans i18nKey='home:enterWorkspaceLabel' />
                      <ArrowRightIcon className='ml-3 h-5 w-5' />
                    </>
                  )}
                </button>
              </div>
            </div>
          ) : (
            <div className='w-full max-w-2xl text-center'>
              <h1 className='text-xl font-bold text-blue-900'>
                <Trans i18nKey='home:noAvailableInvites' />
              </h1>
              <p className='mt-2 text-gray-600'>
                <Trans i18nKey='home:noActivableWorkspace' />
              </p>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};
